import ApolloLogo from '@assets/platforms/apollo.svg';
import GeminiLogo from '@assets/platforms/gemini.svg';
import citiesDataRaw from '@assets/cities-min.json';
import { Button } from '@components/Button';
import { IconButton } from '@components/IconButton';
import type { MenuSelectItem } from '@components/MenuSelect';
import { ModalLayout } from '@components/ModalLayout';
import { NumericStepper } from '@components/NumericStepper';
import { SegmentedControl } from '@components/SegmentedControl';
import { Tooltip } from '@components/Tooltip';
import { TypeAhead } from '@components/TypeAhead';
import { IconAdd, IconClear, MagicSearchIcon } from '@components/icons';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './GenerateMoreModal.module.scss';

// City data structure from cities-min.json
interface CityDataRaw {
  id: number;
  name: string;
  country: string;
  admin1: string | null;
  pop: string;
}

// City data structure
export interface CityData {
  id: number;
  name: string;
  region: string;
  country: string;
  population: number;
}

// Pipeline type for lead generation
export type PipelineType = 'gemini' | 'apollo';

// City entry in the table (after being added)
export interface CityEntry extends CityData {
  numLeads: number;
  status: 'pending' | 'complete';
  pipelineType: PipelineType | null;
}

// Parse and convert cities data
const parseCities = (rawCities: CityDataRaw[]): CityData[] => {
  return rawCities.map((city) => ({
    id: city.id,
    name: city.name,
    region: city.admin1 || city.country,
    country: city.country,
    population: parseInt(city.pop, 10),
  }));
};

// Load cities data
const citiesData: CityData[] = parseCities(citiesDataRaw as CityDataRaw[]);

// Convert city data to MenuSelectItem format
const cityToMenuItem = (city: CityData): MenuSelectItem => ({
  value: String(city.id),
  label: `${city.name} - ${city.region} - ${city.country}`,
  subtitle: `Population: ${city.population.toLocaleString()}`,
});

export interface GenerateMoreModalProps {
  /** Whether the modal is open */
  isOpen: boolean;

  /** Close handler */
  onClose: () => void;

  /** Type name to display in the title (e.g., "Dental Clinics") */
  typeName: string;

  /** Generate handler - called when user clicks Save */
  onGenerate?: (cities: CityEntry[]) => void | Promise<void>;
}

export const GenerateMoreModal = ({
  isOpen,
  onClose,
  typeName,
  onGenerate,
}: GenerateMoreModalProps) => {
  const { t } = useTranslation();
  const [selectedCityId, setSelectedCityId] = useState<string | null>(null);
  const [cities, setCities] = useState<CityEntry[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [typeAheadKey, setTypeAheadKey] = useState(0);

  const handleNumLeadsChange = useCallback((entryId: string, newValue: number) => {
    setCities((prev) =>
      prev.map((c) => (String(c.id) === entryId ? { ...c, numLeads: newValue } : c))
    );
  }, []);

  const handlePipelineChange = useCallback((entryId: string, value: string) => {
    setCities((prev) =>
      prev.map((c) =>
        String(c.id) === entryId ? { ...c, pipelineType: value as PipelineType } : c
      )
    );
  }, []);

  // Early return AFTER all hooks
  if (!isOpen) return null;

  const handleAddCity = () => {
    if (!selectedCityId) return;

    const cityData = citiesData.find((c) => String(c.id) === selectedCityId);
    if (!cityData) return;

    // Check if city is already added
    if (cities.some((c) => c.id === cityData.id)) {
      setSelectedCityId(null);
      return;
    }

    const newEntry: CityEntry = {
      ...cityData,
      numLeads: 6, // Default number of leads
      status: 'pending',
      pipelineType: null,
    };

    setCities([...cities, newEntry]);
    setSelectedCityId(null);
    setTypeAheadKey((prev) => prev + 1); // Force TypeAhead to remount and clear
  };

  const handleRemoveCity = (entryId: string) => {
    setCities(cities.filter((c) => String(c.id) !== entryId));
  };

  const handleSave = async () => {
    if (cities.length === 0) return;

    try {
      setIsSaving(true);

      // Call the onGenerate callback if provided (will handle Firebase saving in parent)
      if (onGenerate) {
        await onGenerate(cities);
      }

      handleClose();
    } catch (error) {
      console.error('Error saving requests:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    handleClose();
  };

  const handleClose = () => {
    setCities([]);
    setSelectedCityId(null);
    onClose();
  };

  const handleBackdropKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  // Convert cities to menu items, excluding already added cities
  const availableCities = citiesData
    .filter((city) => !cities.some((c) => c.id === city.id))
    .map(cityToMenuItem);

  const hasCities = cities.length > 0;
  const allPipelinesSelected = cities.every((c) => c.pipelineType !== null);

  return (
    <div
      className={styles.backdrop}
      onClick={handleClose}
      onKeyDown={handleBackdropKeyDown}
      role="presentation"
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- dialog needs event handlers to prevent backdrop click propagation */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <ModalLayout
          title={`${typeName}: Generate More Leads`}
          onDismiss={handleClose}
          showFooter={true}
          primaryActionText={isSaving ? 'Saving...' : 'Save'}
          secondaryActionText="Cancel"
          onPrimaryAction={handleSave}
          onSecondaryAction={handleCancel}
          primaryActionDisabled={!hasCities || !allPipelinesSelected || isSaving}
          width={800}
        >
          <div className={styles.modal}>
            {/* Search Section */}
            <div className={styles.search}>
              <div className={styles.typeAheadWrapper}>
                <TypeAhead
                  key={typeAheadKey}
                  items={availableCities}
                  value={selectedCityId}
                  onChange={setSelectedCityId}
                  placeholder="Search for a city..."
                  maxHeight={200}
                  clearable
                />
              </div>
              <Button
                variant="primary"
                size="regular"
                leadingIcon={<IconAdd size={18} />}
                onClick={handleAddCity}
                disabled={!selectedCityId}
              >
                Add
              </Button>
            </div>

            {/* Results Area */}
            <div className={styles.results}>
              {!hasCities ? (
                // Empty State
                <div className={styles.empty}>
                  <MagicSearchIcon width={80} height={80} />
                  <p className={styles.emptyText}>Enter a city name above to get started</p>
                </div>
              ) : (
                // Cities Table
                <div className={styles.table}>
                  {/* Table Header */}
                  <div className={styles.tableHeader}>
                    <div className={`${styles.tableCell} ${styles.cellId}`}>ID</div>
                    <div className={`${styles.tableCell} ${styles.cellName}`}>Name</div>
                    <div className={`${styles.tableCell} ${styles.cellType}`}>Pipeline</div>
                    <div className={`${styles.tableCell} ${styles.cellLeads}`}>Number of Leads</div>
                    <div className={`${styles.tableCell} ${styles.cellActions}`}></div>
                  </div>

                  {/* Table Body */}
                  <div className={styles.tableBody}>
                    {cities.map((city) => (
                      <div key={city.id} className={styles.tableRow}>
                        <div className={`${styles.tableCell} ${styles.cellId}`}>{city.id}</div>
                        <div className={`${styles.tableCell} ${styles.cellName}`}>
                          {city.name} ({city.country})
                        </div>
                        <div className={`${styles.tableCell} ${styles.cellType}`}>
                          <SegmentedControl
                            segments={[
                              {
                                value: 'gemini',
                                icon: <Tooltip text={t('generateMore.pipelineGemini')}><img src={GeminiLogo} alt="Gemini" width={18} height={18} /></Tooltip>,
                                activeIcon: <Tooltip text={t('generateMore.pipelineGemini')}><img src={GeminiLogo} alt="Gemini" width={18} height={18} style={{ filter: 'brightness(0) invert(1)' }} /></Tooltip>,
                                ariaLabel: 'Gemini',
                              },
                              {
                                value: 'apollo',
                                icon: <Tooltip text={t('generateMore.pipelineApollo')}><img src={ApolloLogo} alt="Apollo" width={18} height={18} /></Tooltip>,
                                activeIcon: <Tooltip text={t('generateMore.pipelineApollo')}><img src={ApolloLogo} alt="Apollo" width={18} height={18} style={{ filter: 'brightness(0) invert(1)' }} /></Tooltip>,
                                ariaLabel: 'Apollo',
                              },
                            ]}
                            value={city.pipelineType ?? ''}
                            onChange={(val) => handlePipelineChange(String(city.id), val)}
                          />
                        </div>
                        <div className={`${styles.tableCell} ${styles.cellLeads}`}>
                          <NumericStepper
                            value={city.numLeads}
                            onChange={(value) => handleNumLeadsChange(String(city.id), value)}
                            min={1}
                            max={50}
                          />
                        </div>
                        <div className={`${styles.tableCell} ${styles.cellActions}`}>
                          <IconButton
                            size="small"
                            aria-label={`Remove ${city.name}`}
                            onClick={() => handleRemoveCity(String(city.id))}
                            className={styles.removeButton}
                          >
                            <IconClear />
                          </IconButton>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </ModalLayout>
      </div>
    </div>
  );
};
